import type { AdminAgentFamily, AdminAgentInstance } from "@/queries/adminAgents";

/**
 * Render a single override / default value in a stable, compact form.
 * The admin UI shows config values as text — arrays become "[a, b]",
 * nulls become "—", everything else falls back to JSON.stringify.
 */
export function formatConfigValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return `[${value.map((v) => formatConfigValue(v)).join(", ")}]`;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Sum of effective daily_budget_usd over the enabled instances of a
 * family, expressed as a string with 2 decimal places. Mirrors the
 * server-side aggregate used in the response, but recomputed locally
 * so the family-edit modal can preview cascade impact before saving.
 */
export function aggregateEnabledBudget(
  instances: AdminAgentInstance[],
  skill: string,
  familyDefaults: Record<string, unknown>,
  overridesByInstance?: Map<string, Record<string, unknown>>,
): string {
  let total = 0;
  for (const inst of instances) {
    if (inst.skill !== skill || !inst.enabled) continue;
    const overrides = overridesByInstance?.get(inst.instance_id) ?? inst.overrides;
    const effective = { ...familyDefaults, ...overrides };
    const raw = effective["daily_budget_usd"];
    const parsed = typeof raw === "string" ? parseFloat(raw) : typeof raw === "number" ? raw : 0;
    if (Number.isFinite(parsed)) total += parsed;
  }
  return total.toFixed(2);
}

/**
 * Count of enabled instances that belong to a given family.
 */
export function countEnabledInFamily(
  instances: AdminAgentInstance[],
  skill: string,
): number {
  return instances.filter((i) => i.skill === skill && i.enabled).length;
}

/**
 * Disabled-but-declared templates for a family, used by the
 * Add-instance dialog. ADR-0013 forbids runtime invention of new
 * instance IDs, so the UI only surfaces existing templates that
 * are currently disabled.
 */
export function disabledTemplatesForFamily(
  instances: AdminAgentInstance[],
  skill: string,
): AdminAgentInstance[] {
  return instances.filter((i) => i.skill === skill && !i.enabled);
}

/**
 * True if `instance` is the only enabled member of its family. Used
 * by the disable-confirmation dialog to add a warning row.
 */
export function isLastEnabledOfFamily(
  instances: AdminAgentInstance[],
  instance: AdminAgentInstance,
): boolean {
  if (!instance.enabled) return false;
  return countEnabledInFamily(instances, instance.skill) === 1;
}

/**
 * Filter `effective` down to override keys only — for badge rendering
 * on the family overview row.
 */
export function listOverrideKeys(instance: AdminAgentInstance): string[] {
  return Object.keys(instance.overrides ?? {});
}

/**
 * Pretty-format the family card's "X of Y enabled" sentence.
 */
export function formatInstanceCount(family: AdminAgentFamily): {
  enabled: number;
  total: number;
} {
  return { enabled: family.enabled_instance_count, total: family.instance_count };
}
